import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AdminProvider, useAdmin } from "./context/AdminContext";
import { SettingsProvider } from "./context/SettingsContext";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Categories from "./pages/Categories";
import Lesson from "./pages/Lesson";
import Lessons from "./pages/Lessons";
import LessonPlayer from "./pages/LessonPlayer";
import MathReference from "./pages/MathReference";
import Tutorials from "./pages/Tutorials";
import AITutor from "./pages/AITutor";
import Profile from "./pages/Profile";
import Leaderboard from "./pages/Leaderboard";
import Membership from "./pages/Membership";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import About from "./pages/About";
import Pricing from "./pages/Pricing";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import Layout from "./components/Layout";
import CookieConsent from "./components/CookieConsent";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminQuestions from "./pages/admin/AdminQuestions";
import AdminMemberships from "./pages/admin/AdminMemberships";
import AdminDistricts from "./pages/admin/AdminDistricts";
import TeacherLogin from "./pages/teacher/TeacherLogin";
import TeacherRegister from "./pages/teacher/TeacherRegister";
import TeacherDashboard from "./pages/teacher/TeacherDashboard";
import ClassDetail from "./pages/teacher/ClassDetail";
import CreateAssignment from "./pages/teacher/CreateAssignment";
import ReteachPack from "./pages/teacher/ReteachPack";
import MockTest from "./pages/MockTest";
import DistrictLogin from "./pages/district/DistrictLogin";
import DistrictRegister from "./pages/district/DistrictRegister";
import DistrictDashboard from "./pages/district/DistrictDashboard";
import ParentDashboard from "./pages/parent/ParentDashboard";
import StudyPlans from "./pages/StudyPlans";
import Compliance from "./pages/Compliance";
import TexasSchools from "./pages/TexasSchools";
import "./App.css";

const Spinner = ({ dark = false }) => (
  <div className={`min-h-screen flex items-center justify-center ${dark ? "bg-slate-900" : "bg-slate-50"}`}>
    <div className={`w-10 h-10 border-4 ${dark ? "border-indigo-500" : "border-lime-500"} border-t-transparent rounded-full animate-spin`} />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { admin, loading } = useAdmin();
  if (loading) return <Spinner dark />;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
};

const AdminPublicRoute = ({ children }) => {
  const { admin, loading } = useAdmin();
  if (loading) return <Spinner dark />;
  if (admin) return <Navigate to="/admin/dashboard" replace />;
  return children;
};

const TeacherRoute = ({ children }) => {
  const token = localStorage.getItem("teacherToken");
  if (!token) return <Navigate to="/teacher/login" replace />;
  return children;
};

const TeacherPublicRoute = ({ children }) => {
  const token = localStorage.getItem("teacherToken");
  if (token) return <Navigate to="/teacher/dashboard" replace />;
  return children;
};

const DistrictRoute = ({ children }) => {
  const token = localStorage.getItem("teacherToken");
  const teacher = JSON.parse(localStorage.getItem("teacher") || "{}");
  if (!token || teacher?.role !== "district_admin") return <Navigate to="/district/login" replace />;
  return children;
};

const DistrictPublicRoute = ({ children }) => {
  const token = localStorage.getItem("teacherToken");
  const teacher = JSON.parse(localStorage.getItem("teacher") || "{}");
  if (token && teacher?.role === "district_admin") return <Navigate to="/district/dashboard" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <AdminProvider>
        <SettingsProvider>
          <BrowserRouter>
          <Toaster
            position="top-center"
            richColors
            toastOptions={{
              style: {
                fontFamily: "Nunito, sans-serif",
                fontWeight: 700,
                borderRadius: "1rem",
              },
            }}
          />
          <CookieConsent />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/about" element={<About />} />
            <Route path="/tutorials" element={<Tutorials />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/compliance" element={<Compliance />} />
            <Route path="/texas-schools" element={<TexasSchools />} />

            {/* Student Protected Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
            <Route path="/categories" element={<ProtectedRoute><Layout><Categories /></Layout></ProtectedRoute>} />
            <Route path="/lesson/:categoryId" element={<ProtectedRoute><Layout><Lesson /></Layout></ProtectedRoute>} />
            <Route path="/lessons" element={<ProtectedRoute><Layout><Lessons /></Layout></ProtectedRoute>} />
            <Route path="/lessons/:lessonId" element={<ProtectedRoute><Layout><LessonPlayer /></Layout></ProtectedRoute>} />
            <Route path="/reference/math" element={<ProtectedRoute><Layout><MathReference /></Layout></ProtectedRoute>} />
            <Route path="/tutor" element={<ProtectedRoute><Layout><AITutor /></Layout></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><Layout><Leaderboard /></Layout></ProtectedRoute>} />
            <Route path="/membership" element={<ProtectedRoute><Layout><Membership /></Layout></ProtectedRoute>} />
            <Route path="/mock-test/:category" element={<ProtectedRoute><MockTest /></ProtectedRoute>} />
            <Route path="/study-plans" element={<ProtectedRoute><Layout><StudyPlans /></Layout></ProtectedRoute>} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminPublicRoute><AdminLogin /></AdminPublicRoute>} />
            <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
            <Route path="/admin/questions" element={<AdminRoute><AdminQuestions /></AdminRoute>} />
            <Route path="/admin/memberships" element={<AdminRoute><AdminMemberships /></AdminRoute>} />
            <Route path="/admin/districts" element={<AdminRoute><AdminDistricts /></AdminRoute>} />

            {/* Teacher Routes — all require auth */}
            <Route path="/teacher/login" element={<TeacherPublicRoute><TeacherLogin /></TeacherPublicRoute>} />
            <Route path="/teacher/register" element={<TeacherPublicRoute><TeacherRegister /></TeacherPublicRoute>} />
            <Route path="/teacher/dashboard" element={<TeacherRoute><TeacherDashboard /></TeacherRoute>} />
            <Route path="/teacher/classes" element={<TeacherRoute><TeacherDashboard /></TeacherRoute>} />
            <Route path="/teacher/classes/new" element={<TeacherRoute><TeacherDashboard /></TeacherRoute>} />
            <Route path="/teacher/classes/:classId" element={<TeacherRoute><ClassDetail /></TeacherRoute>} />
            <Route path="/teacher/assignments" element={<TeacherRoute><TeacherDashboard /></TeacherRoute>} />
            <Route path="/teacher/assignments/new" element={<TeacherRoute><CreateAssignment /></TeacherRoute>} />
            <Route path="/teacher/assignments/:assignmentId" element={<TeacherRoute><TeacherDashboard /></TeacherRoute>} />
            <Route path="/teacher/reteach" element={<TeacherRoute><ReteachPack /></TeacherRoute>} />
            <Route path="/teacher/analytics" element={<TeacherRoute><TeacherDashboard /></TeacherRoute>} />
            <Route path="/teacher/roster" element={<TeacherRoute><TeacherDashboard /></TeacherRoute>} />

            {/* District Routes */}
            <Route path="/district/login" element={<DistrictPublicRoute><DistrictLogin /></DistrictPublicRoute>} />
            <Route path="/district/register" element={<DistrictPublicRoute><DistrictRegister /></DistrictPublicRoute>} />
            <Route path="/district/dashboard" element={<DistrictRoute><DistrictDashboard /></DistrictRoute>} />

            {/* Parent Routes */}
            <Route path="/parent/dashboard" element={<ParentDashboard />} />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrowserRouter>
        </SettingsProvider>
      </AdminProvider>
    </AuthProvider>
  );
}

export default App;
